// AddressField — an address box that is a PLAIN TEXT INPUT FIRST, and a Google
// Places autocomplete only if it can be.
//
// ── The load-bearing property ───────────────────────────────────────────────
//
// Most installs have no `google_maps_api_key`. `server/database.js:1120` seeds
// it as the empty string, so "no key" is the DEFAULT state of a fresh box, not
// an error state. A landscaping company that never signs up for a Google Cloud
// billing account must be able to type an address into every form, forever.
//
// So the input is rendered unconditionally, with the caller's `value` and
// `onChange` wired straight to it, and nothing about the enhancement is on the
// render path. No key, an empty key, a key the settings fetch never delivered,
// a script blocked by a corporate proxy, a key without the Places API enabled,
// a constructor that throws — every one of those leaves an ordinary
// <FwInput> behaving exactly as it does in the fields beside it. That is
// pinned by addressField.test.js, which is written so that deleting the
// guards makes it red rather than merely different.
//
// ── Where the key comes from ────────────────────────────────────────────────
//
// NOT from `GET /api/settings/public`: that route returns a six-key branding
// allow-list (brand_favicon, brand_name, brand_short, brand_logo, brand_color,
// appearance_preset — `server/routes/settings.js`) and `google_maps_api_key` is
// not one of them. The key lives in the AUTHENTICATED `GET /api/settings`,
// which returns the whole settings table.
//
// This component makes NO request of its own. `client/src/App.js`
// SettingsProvider already fetches `GET /api/settings` once per page for a
// signed-in user and puts the flat object in SettingsContext, which `Gate`
// reads to decide the cutover — so every rebuild screen is already inside that
// provider and the key is already in memory. Reading it from context instead of
// fetching is strictly less surface than the classic component, whose own
// header (client/src/components/AddressAutocomplete/AddressAutocomplete.js:8)
// records two instances on one page making two authenticated reads of a table
// that also holds smtp_password.
//
// The consequence to know: SettingsProvider fetches at mount, so a key an admin
// enters in Office → Settings → Company reaches these fields on the next page
// load, not the same second. A field that is already a working text box is the
// right thing to be wrong about for a minute.
//
// ── Loaded once, and never twice ────────────────────────────────────────────
//
// `loadPromise` is a module singleton and is NOT cleared on failure. The
// classic component clears its loading flag in `script.onerror`, so the next
// mount appends a second <script> to the same dead URL — and on a wrong or
// referrer-restricted key that is every address form the user opens for the
// rest of the session. A failed load here is remembered: one script tag, one
// attempt, and a text box that works regardless.

import React, { useEffect, useRef, useState } from 'react';
import { useSettings } from '../../App';
import { FwField, FwInput } from '../../ui/fw';

const MAPS_SRC = 'https://maps.googleapis.com/maps/api/js';
const SCRIPT_MARK = 'data-fw-maps';

/** Module singleton: the one script load for this page. See the header. */
let loadPromise = null;

/** Test seam only — the singleton above is why a suite must reset it. */
export function __resetMapsLoader() {
  loadPromise = null;
}

/** Is the Places constructor actually callable right now? */
export function placesReady(win = typeof window === 'undefined' ? null : window) {
  const places = win && win.google && win.google.maps && win.google.maps.places;
  return !!(places && typeof places.Autocomplete === 'function');
}

/**
 * The configured key, or null. Accepts both shapes the settings routes answer
 * with — the authenticated route's flat object and `/public`'s [{key, value}]
 * rows — because SettingsContext holds whichever one was reachable at mount.
 * An empty or whitespace value is "not configured", which is what the seeded
 * default is.
 */
export function mapsKeyFrom(settings) {
  if (!settings) return null;
  const raw = Array.isArray(settings)
    ? (settings.find((r) => r && r.key === 'google_maps_api_key') || {}).value
    : settings.google_maps_api_key;
  const key = typeof raw === 'string' ? raw.trim() : '';
  return key || null;
}

/**
 * Append the Maps script at most once per page and resolve whether Places is
 * usable. Never rejects: a caller must not have to catch to keep a text box.
 */
export function loadPlaces(apiKey) {
  if (placesReady()) return Promise.resolve(true);
  if (loadPromise) return loadPromise;
  if (!apiKey || typeof document === 'undefined') return Promise.resolve(false);

  loadPromise = new Promise((resolve) => {
    const done = () => resolve(placesReady());
    let script = document.querySelector(`script[${SCRIPT_MARK}]`);
    if (!script) {
      script = document.createElement('script');
      script.src = `${MAPS_SRC}?key=${encodeURIComponent(apiKey)}&libraries=places`;
      script.async = true;
      script.setAttribute(SCRIPT_MARK, '1');
      document.head.appendChild(script);
    }
    script.addEventListener('load', done);
    script.addEventListener('error', () => resolve(false));
  });
  return loadPromise;
}

/**
 * A Google place → the fields this company's records actually have.
 *
 * Kept identical to the classic component's parse, which is Connecticut-shaped
 * on purpose and has its own suite explaining why
 * (components/AddressAutocomplete/AddressAutocomplete.test.js): `city` walks
 * locality → sublocality_level_1 → administrative_area_level_2 because many
 * addresses here are in unincorporated village areas Google returns with no
 * locality at all; `state` and `country` take short_name (CT, US) while the
 * rest take long_name; `street` joins two components and is trimmed so a rural
 * address with no street number does not come back with a leading space.
 *
 * Returns null for anything that is not a resolved place — Google fires
 * `place_changed` for a bare Enter press too, with only a `name`.
 */
export function parsePlace(place) {
  if (!place || !Array.isArray(place.address_components)) return null;
  const find = (type) => place.address_components.find((c) => c && Array.isArray(c.types) && c.types.includes(type));
  const long = (type) => { const c = find(type); return c ? (c.long_name || '') : ''; };
  const short = (type) => { const c = find(type); return c ? (c.short_name || '') : ''; };
  let lat = null;
  let lng = null;
  try {
    const loc = place.geometry && place.geometry.location;
    if (loc && typeof loc.lat === 'function') lat = loc.lat();
    if (loc && typeof loc.lng === 'function') lng = loc.lng();
  } catch (_) { /* a place with no geometry is still a usable address */ }
  return {
    formatted: place.formatted_address || '',
    street: `${long('street_number')} ${long('route')}`.trim(),
    city: long('locality') || long('sublocality_level_1') || long('administrative_area_level_2'),
    state: short('administrative_area_level_1'),
    zip: long('postal_code'),
    country: short('country'),
    lat,
    lng,
  };
}

/**
 * Is Google's suggestion list on screen?
 *
 * Google renders `.pac-container` into <body> and hides it with an inline
 * `display: none` when closed. Enter inside the input while it is OPEN picks a
 * suggestion — and, in a <form>, ALSO submits the form, so choosing an address
 * saves the record. That is the one keystroke this component has to intercept,
 * and only while the list is up: swallowing Enter unconditionally would take
 * submit-on-Enter away from a field that has no suggestions to offer.
 */
export function suggestionsOpen(doc = typeof document === 'undefined' ? null : document) {
  if (!doc || typeof doc.querySelectorAll !== 'function') return false;
  return Array.from(doc.querySelectorAll('.pac-container')).some((el) => {
    const style = (el.getAttribute && el.getAttribute('style')) || '';
    if (/display\s*:\s*none/i.test(style)) return false;
    return el.children.length > 0;
  });
}

const FAILED_HINT = 'Address suggestions are unavailable — type the address in full.';

/**
 * @param {object} props
 *   label, hint, error, required, htmlFor — passed to FwField unchanged
 *   value, onChange                       — the input's own value and change
 *                                           event, exactly as FwInput takes them
 *   onPlace(parsed)                        — called when a suggestion is chosen,
 *                                           with the parsePlace() shape. Without
 *                                           it the formatted address is sent
 *                                           through onChange instead.
 */
export default function AddressField({
  label,
  hint,
  error,
  required,
  htmlFor,
  value,
  onChange,
  onPlace,
  placeholder,
  autoComplete = 'street-address',
  ...rest
}) {
  const { settings } = useSettings();
  const apiKey = mapsKeyFrom(settings);
  const inputRef = useRef(null);
  const acRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attached, setAttached] = useState(false);

  // The place_changed listener is attached ONCE per instance, so without these
  // refs it keeps the callbacks from the render it was attached in — and those
  // closed over the parent's form state as it was then. Picking an address
  // then fired setForm({ ...stale, address }) and wiped everything typed since.
  // That bug is v2.8.28 in the classic component and its comment is worth
  // reading; the fix is the same here because the cause is.
  const onChangeRef = useRef(onChange);
  const onPlaceRef = useRef(onPlace);
  useEffect(() => { onChangeRef.current = onChange; });
  useEffect(() => { onPlaceRef.current = onPlace; });

  useEffect(() => {
    if (!apiKey) return undefined;
    let cancelled = false;
    loadPlaces(apiKey).then((ok) => {
      if (cancelled) return;
      if (ok) setReady(true); else setFailed(true);
    });
    return () => { cancelled = true; };
  }, [apiKey]);

  useEffect(() => {
    if (!ready || acRef.current || !inputRef.current) return undefined;
    let ac;
    try {
      ac = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address', 'geometry'],
      });
      ac.addListener('place_changed', () => {
        let parsed = null;
        try { parsed = parsePlace(ac.getPlace()); } catch (_) { parsed = null; }
        if (!parsed) return;
        if (onPlaceRef.current) onPlaceRef.current(parsed);
        else if (onChangeRef.current) onChangeRef.current({ target: { value: parsed.formatted } });
      });
    } catch (_) {
      // A library that is present but not behaving is the same outcome as one
      // that never loaded: a plain text box, and a hint saying so.
      setFailed(true);
      return undefined;
    }
    acRef.current = ac;
    setAttached(true);
    return () => {
      acRef.current = null;
      setAttached(false);
      try { window.google.maps.event.clearInstanceListeners(ac); } catch (_) { /* older builds */ }
    };
  }, [ready]);

  const onKeyDown = (e) => {
    if (attached && e.key === 'Enter' && suggestionsOpen()) e.preventDefault();
  };

  const shownHint = failed ? [hint, FAILED_HINT].filter(Boolean).join(' ') : hint;

  return (
    <FwField label={label} hint={shownHint} error={error} required={required} htmlFor={htmlFor} {...rest}>
      <FwInput
        ref={inputRef}
        value={value || ''}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete={autoComplete}
        data-address-autocomplete={attached ? 'on' : 'off'}
      />
    </FwField>
  );
}
